<script>
  import * as _ from "lodash";
  import { createEventDispatcher } from "svelte";

  const dispatch = createEventDispatcher();

  let tag = "";
  let arrelementsmatch = [];
  let regExpEscape = s => {
    return s.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
  };

  export let tags;
  export let addKeys = null;
  export let maxTags = null;
  export let onlyUnique;
  export let removeKeys;
  export let placeholder;
  export let allowPaste = null;
  export let allowDrop = null;
  export let splitWith = null;
  export let autoComplete = null;
  export let autoCompleteKey = null;
  export let name = null;
  export let id = null;
  export let allowBlur = null;
  export let disable;
  export let minChars;
  export let dropdownItemTemplateCB = null;
  export let dropdownItemTemplateComponent = null;
  export let addFromAutocompleteOnly = null;
  export let inputErrors = null;
  export let submitting = null;
  export let dropdownClassName = "";
  export let autocapitalize = "off";
  export let autocorrect = "off";
  export let autocomplete = "off";
  export let clickedIntoInput = null;
  export let leftInput = null;

  let focused = false;

  $: tags = tags || [];
  $: addKeys = addKeys || [13];
  $: maxTags = maxTags || false;
  $: onlyUnique = onlyUnique || false;
  $: removeKeys = removeKeys || [8];
  $: placeholder = placeholder || "";
  $: allowPaste = allowPaste || false;
  $: allowDrop = allowDrop || false;
  $: splitWith = splitWith || ",";
  $: autoComplete = autoComplete || false;
  $: autoCompleteKey = autoCompleteKey || false;
  $: name = name || "svelte-tags-input";
  $: id = id || uniqueID();
  $: allowBlur = allowBlur || false;
  $: disable = disable || false;
  $: minChars = minChars || 1;
  $: addFromAutocompleteOnly = addFromAutocompleteOnly || false;
  $: inputErrors = inputErrors;
  $: inputClass = inputErrors
    ? focused
      ? "svelte-tags-input-layout-border"
      : "fizz-bordered fizz-input-error"
    : "svelte-tags-input-layout-border";

  $: matchsID = id + "_matchs";

  let storePlaceholder = placeholder;

  let wasBackspacingText = false;
  function clearBackspacedFlag() {
    wasBackspacingText = false;
  }
  const debouncedClearBackspacedFlag = _.debounce(clearBackspacedFlag, 1000);

  function setTag(input) {
    if (input.keyCode === 13) {
      dispatch("keyenter", input.target.value);
      input.preventDefault();
    }

    if (input.keyCode === 8) {
      if (tag && tag.length > 0) {
        wasBackspacingText = true;
        debouncedClearBackspacedFlag();
      }
    }

    const currentTag = input.target.value;

    if (addKeys) {
      addKeys.forEach(function (key) {
        if (key === input.keyCode) {
          if (currentTag) input.preventDefault();
          switch (input.keyCode) {
            case 9:
              // TAB add first element on the autoComplete list
              if (autoComplete && document.getElementById(matchsID)) {
                addTag(document.getElementById(matchsID).querySelectorAll("li")[0].textContent);
              } else {
                if (!addFromAutocompleteOnly) addTag(currentTag);
              }
              break;
            default:
              if (!addFromAutocompleteOnly) {
                addTag(currentTag);
              } else if (arrelementsmatch.length) {
                addTag(arrelementsmatch[0].label);
              }
              break;
          }
        }
      });
    }

    if (removeKeys) {
      if (input.keyCode === 8 && wasBackspacingText === true) {
        return;
      }

      removeKeys.forEach(function (key) {
        if (key === input.keyCode && tag === "") {
          let tagRemoved = tags.pop();
          tags = tags;

          dispatch("tags", {
            tags: tags,
          });
          dispatch("tagRemoved", tagRemoved);

          arrelementsmatch = [];
          document.getElementById(id).readOnly = false;
          placeholder = storePlaceholder;
          document.getElementById(id).focus();
        }
      });
    }

    // ArrowDown : focus on first element of the autocomplete
    if (input.keyCode === 40 && autoComplete && document.getElementById(matchsID)) {
      input.preventDefault();
      if (document.getElementById(matchsID).querySelector("li:first-child")) {
        document.getElementById(matchsID).querySelector("li:first-child").focus();
      } else if (document.getElementById(matchsID).querySelector("li")) {
        document.getElementById(matchsID).querySelector("li").focus();
      }
    } // ArrowUp : focus on last element of the autocomplete
    else if (input.keyCode === 38 && autoComplete && document.getElementById(matchsID)) {
      input.preventDefault();
      if (document.getElementById(matchsID).querySelector("li:last-child")) {
        document.getElementById(matchsID).querySelector("li:last-child").focus();
      }
    }
  }

  function addTag(currentTag) {
    if (typeof currentTag === "object" && currentTag !== null) {
      if (!autoCompleteKey) {
        return console.error(
          "'autoCompleteKey' is necessary if 'autoComplete' result is an array of objects"
        );
      }

      var currentObjTags = currentTag;
      currentTag = currentTag[autoCompleteKey].trim();
    } else {
      currentTag = currentTag.trim();
    }

    if (currentTag == "") return;
    if (maxTags && tags.length == maxTags) return;
    if (onlyUnique && tags.includes(currentTag)) return;

    let tagToAdd = currentObjTags ? currentObjTags : currentTag;
    tags.push(tagToAdd);
    tags = tags;
    tag = "";

    dispatch("tags", {
      tags: tags,
    });
    dispatch("tagAdded", tagToAdd);

    // Hide autocomplete list
    // Focus on svelte tags input
    arrelementsmatch = [];
    document.getElementById(id).focus();

    if (maxTags && tags.length == maxTags) {
      document.getElementById(id).readOnly = true;
      placeholder = "";
    }
  }

  function removeTag(i) {
    let tagToRemove = tags[i];
    tags.splice(i, 1);
    tags = tags;

    dispatch("tags", {
      tags: tags,
    });
    dispatch("tagRemoved", tagToRemove);

    // Hide autocomplete list
    // Focus on svelte tags input
    arrelementsmatch = [];
    document.getElementById(id).readOnly = false;
    placeholder = storePlaceholder;
    document.getElementById(id).focus();
  }

  function onPaste(e) {
    if (!allowPaste) return;

    e.preventDefault();

    const data = getClipboardData(e);
    const tags = splitTags(data).map(tag => addTag(tag));
  }

  function onDrop(e) {
    if (!allowDrop) return;

    e.preventDefault();

    const data = e.dataTransfer.getData("Text");
    const tags = splitTags(data).map(tag => addTag(tag));
  }

  function onFocus(e) {
    dispatch("focus", e.detail);
    focused = true;
  }

  function onBlur(event, inputTag) {
    focused = false;
    if (!document.getElementById(matchsID) && allowBlur) {
      event.preventDefault();
      if (!addFromAutocompleteOnly) addTag(inputTag);
    }
    if (addFromAutocompleteOnly && document.getElementById(matchsID)) {
    } else if (
      addFromAutocompleteOnly &&
      event &&
      event.relatedTarget &&
      event.relatedTarget.nodeName !== "LI"
    ) {
      arrelementsmatch = [];
      tag = "";
    }
  }

  function getClipboardData(e) {
    if (window.clipboardData) {
      return window.clipboardData.getData("Text");
    }

    if (e.clipboardData) {
      return e.clipboardData.getData("text/plain");
    }

    return "";
  }

  function splitTags(data) {
    return data.split(splitWith).map(tag => tag.trim());
  }

  async function getMatchElements(input) {
    if (!autoComplete) return;

    let autoCompleteValues = [];

    if (Array.isArray(autoComplete)) {
      autoCompleteValues = autoComplete;
    }

    var value = input.target.value;

    if (typeof autoComplete === "function") {
      autoCompleteValues = await autoComplete(value);
    }

    // Escape
    if (value == "" || input.keyCode === 27 || value.length < minChars) {
      arrelementsmatch = [];
      return;
    }

    if (typeof autoCompleteValues[0] === "object" && autoCompleteValues !== null) {
      if (!autoCompleteKey) {
        return console.error(
          "'autoCompleteValue' is necessary if 'autoComplete' result is an array of objects"
        );
      }

      var matchs = autoCompleteValues.map(matchTag => {
        let search;
        if (dropdownItemTemplateCB) {
          search = dropdownItemTemplateCB(matchTag);
        } else {
          search = matchTag[autoCompleteKey].replace(
            RegExp(regExpEscape(value.toLowerCase()), "i"),
            "<strong>$&</strong>"
          );
        }
        return {
          label: matchTag,
          search: search,
        };
      });
    } else {
      var matchs = autoCompleteValues
        .filter(e => e.toLowerCase().includes(value.toLowerCase()))
        .map(matchTag => {
          return {
            label: matchTag,
            search: matchTag.replace(
              RegExp(regExpEscape(value.toLowerCase()), "i"),
              "<strong>$&</strong>"
            ),
          };
        });
    }

    if (onlyUnique === true && !autoCompleteKey) {
      matchs = matchs.filter(tag => !tags.includes(tag.label));
    }

    arrelementsmatch = matchs;
  }

  const getMatchElementsDebounced = _.debounce(getMatchElements, 250);

  function navigateAutoComplete(autoCompleteIndex, autoCompleteLength, autoCompleteElement) {
    if (!autoComplete) return;

    event.preventDefault();

    // ArrowDown
    if (event.keyCode === 40) {
      // Last element on the list ? Go to the first
      if (autoCompleteIndex + 1 === autoCompleteLength) {
        document.getElementById(matchsID).querySelector("li:first-child").focus();
        return;
      }
      document.getElementById(matchsID).querySelectorAll("li")[autoCompleteIndex + 1].focus();
    } else if (event.keyCode === 38) {
      // ArrowUp
      // First element on the list ? Go to the last
      if (autoCompleteIndex === 0) {
        document.getElementById(matchsID).querySelector("li:last-child").focus();
        return;
      }
      document.getElementById(matchsID).querySelectorAll("li")[autoCompleteIndex - 1].focus();
    } else if (event.keyCode === 13) {
      // Enter
      addTag(autoCompleteElement);
    } else if (event.keyCode === 27) {
      // Escape
      arrelementsmatch = [];
      document.getElementById(id).focus();
    }
  }

  function uniqueID() {
    return "sti_" + Math.random().toString(36).substr(2, 9);
  }

  function handleClickedIntoInput() {
    clickedIntoInput = true;
  }

  function handleLeftInput() {
    leftInput = true;
  }

</script>

<style>
  /* CSS svelte-tags-input */

  .svelte-tags-input,
  .svelte-tags-input-tag,
  .svelte-tags-input-matchs {
    font-size: 14px;
    padding: 2px 5px;
  }
  .svelte-tags-input-tag {
    z-index: 100;
  }

  .svelte-tags-input-matchs {
    z-index: 300;
  }

  /* svelte-tags-input-layout */

  .svelte-tags-input-layout {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -ms-flex-wrap: wrap;
    flex-wrap: wrap;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    padding: 0px 5px 5px 5px;
    background: #fff;
    border-radius: 4px;
  }

  .svelte-tags-input-layout-border {
    border: solid 1px #ccc;
  }

  .svelte-tags-input-focused {
    box-shadow: 0 0 6px #0090c3;
  }

  .svelte-tags-input-layout:focus,
  .svelte-tags-input-layout:hover {
    border: solid 1px #1f9cc6;
  }

  /* svelte-tags-input */

  .svelte-tags-input {
    -webkit-box-flex: 1;
    -ms-flex: 1;
    flex: 1;
    margin: 0;
    margin-top: 6px;
    margin-bottom: 1px;
    padding-left: 10px;
    border: none;
  }

  .svelte-tags-input:focus {
    outline: 0;
  }

  /* svelte-tags-input-tag */

  .svelte-tags-input-tag {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    white-space: nowrap;
    list-style: none;
    background: #e5e5e5;
    color: #000;
    border-radius: 2px;
    margin-right: 5px;
    margin-top: 5px;
  }

  /*.svelte-tags-input-tag:hover {
      background: #CCC;
  }*/

  .svelte-tags-input-tag-remove {
    cursor: pointer;
    padding-left: 4px;
  }

  /* svelte-tags-input-matchs */

  .svelte-tags-input-matchs-parent {
    position: relative;
  }

  .svelte-tags-input-matchs {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    margin: 3px 0;
    padding: 0px;
    background: #fff;
    border: solid 1px #ccc;
    border-radius: 2px;
    max-height: 310px;
    overflow: scroll;
    overflow-x: auto;
  }

  .svelte-tags-input-matchs li {
    list-style: none;
    padding: 5px;
    border-radius: 2px;
    cursor: pointer;
  }

  .svelte-tags-input-matchs li:hover,
  .svelte-tags-input-matchs li:focus {
    background: #3595f6;
    color: #000;
    outline: none;
  }

  /* svelte-tags-input disabled */
  .svelte-tags-input-layout.sti-layout-disable,
  .svelte-tags-input:disabled {
    background: #eaeaea;
    cursor: not-allowed;
  }

  .svelte-tags-input-layout.sti-layout-disable:hover,
  .svelte-tags-input-layout.sti-layout-disable:focus {
    border-color: #ccc;
  }

  .svelte-tags-input-layout.sti-layout-disable .svelte-tags-input-tag {
    background: #aeaeae;
  }

  .svelte-tags-input-layout.sti-layout-disable .svelte-tags-input-tag-remove {
    cursor: not-allowed;
  }

</style>

<div
  class={`svelte-tags-input-layout fizz-stack-4 ${inputClass}`}
  class:svelte-tags-input-focused={focused}
  class:sti-layout-disable={disable}
>
  {#if tags.length > 0}
    {#each tags as tag, i}
      <span class="svelte-tags-input-tag tagify__tag" title={tag[autoCompleteKey]}>
        {#if typeof tag === "string"}
          {tag}
        {:else}
          {tag[autoCompleteKey]}
        {/if}
        {#if !disable}
          <span
            class="svelte-tags-input-tag-remove tagify__tag__removeBtn"
            on:click={() => removeTag(i)}
          >
            &#215;</span
          >
        {/if}
      </span>
    {/each}
  {/if}
  <input
    type="text"
    {id}
    {name}
    {autocapitalize}
    {autocorrect}
    {autocomplete}
    bind:value={tag}
    on:keydown={setTag}
    on:keyup={getMatchElementsDebounced}
    on:paste={onPaste}
    on:drop={onDrop}
    on:focus={onFocus}
    on:blur={e => onBlur(e, tag)}
    class="svelte-tags-input tagify tagify__input"
    {placeholder}
    disabled={disable}
    on:click={handleClickedIntoInput}
    on:mouseleave={handleLeftInput}
  />
</div>

{#if autoComplete && arrelementsmatch.length > 0}
  <div class={dropdownClassName != "" ? dropdownClassName : "svelte-tags-input-matchs-parent"}>
    <ul
      id="{id}_matchs"
      class={dropdownClassName != "" ? dropdownClassName : "svelte-tags-input-matchs"}
    >
      <slot name="dropdown-header" />
      {#each arrelementsmatch as element, index}
        <li
          tabindex="-1"
          on:keydown={() => navigateAutoComplete(index, arrelementsmatch.length, element.label)}
          on:click={() => addTag(element.label)}
        >
          {#if dropdownItemTemplateComponent}
            <svelte:component this={dropdownItemTemplateComponent} {element} />
          {:else}
            {@html element.search}
          {/if}
        </li>
      {/each}
      <slot name="dropdown-footer" />
    </ul>
  </div>
{:else if autoComplete && arrelementsmatch.length === 0 && tag && tag.length > 1}
  <div class="svelte-tags-input-matchs-parent">
    <ul id="{id}_matchs" class="svelte-tags-input-matchs">
      <slot name="dropdown-header" />
      <li>
        {#if submitting}
          <div
            class="fizz-flex-row-8 fizz-align-content-start fizz-highlight-on-hover fizz-inset-squish-16 fizz-icon-spinner-small"
          />
        {:else}
          <div class="fizz-flex-row-8 fizz-align-content-start fizz-inset-squish-16">
            <strong>No Results Found</strong><br />
          </div>
        {/if}
      </li>
      <slot name="dropdown-footer" />
    </ul>
  </div>
{/if}
